import {ProgressAria} from '_components/ProgressAria';
import LFCOkNgPie from '_components/surfaces/LFCOkNgPie';
import LFCSingleValues from '_components/surfaces/LFCSingleValues';
import LFCTitledCard from '_components/surfaces/LFCTitledCard';
import {FORMAT_YYYYMMDDHHmmSS} from '_logics/LFCConst';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import dayjs from 'dayjs';
import {isEmpty, round} from 'lodash';
import {useSnackbar} from 'notistack';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Grid} from '@mui/material';

import {SearchConditions} from '../types';

interface TodaySummaryPanelProps {
  searchConditions: SearchConditions | null;
}

const TodaySummaryPanel: React.FC<TodaySummaryPanelProps> = ({searchConditions}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);
  const [data60351, setData60351] = useState({total_count: '0', ok_count: '0', ng_count: '0'});
  const [data60009, setData60009] = useState({});
  const [data60006, setData60006] = useState({});
  const [dispDate, setDispDate] = useState('');

  /**
   * 初期処理
   */
  useEffect(() => {
    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  /**
   * 検索処理
   */
  useEffect(() => {
    if (searchConditions === null) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    // 対象期間算出
    const now = dayjs();
    let fromDt = dayjs(`${now.format('YYYY-MM-DD')} ${searchConditions.select_time}`);

    // 稼働開始時刻より前の場合、前日の同時刻を開始時刻とする。
    if (searchConditions.select_time > now.format('HH:mm')) {
      // 稼働開始時刻
      fromDt = fromDt.add(-1, 'd');
    }
    const toDt = fromDt.add(1, 'd');

    setDispDate(fromDt.format(FORMAT_YYYYMMDDHHmmSS));

    const parameters = {
      select_datetime_from: fromDt.format(),
      select_datetime_to: toDt.format(),
      work: !isEmpty(searchConditions.work) ? '{' + searchConditions.work.join(',') + '}' : null,
      deviceid: !isEmpty(searchConditions.deviceid)
        ? '{' + searchConditions.deviceid.join(',') + '}'
        : null,
      dongleid: !isEmpty(searchConditions.dongleid)
        ? '{' + searchConditions.dongleid.join(',') + '}'
        : null
    };

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 60351,
        parameters,
        name: `${t('検査数と不良数')}`,
        cancelToken: cancelSource.current.token,
        t
      }).then(datas => {
        setData60351(datas.length > 0 ? datas[0] : []);
      }),
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 60009,
        parameters: {
          working_time: searchConditions.working_time,
          ...parameters
        },
        name: `${t('検査間隔')}`,
        cancelToken: cancelSource.current.token,
        t
      }).then(datas => {
        setData60009(datas.length > 0 ? datas[0] : []);
      }),
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 60006,
        parameters: {
          working_time: searchConditions.working_time,
          target_number: searchConditions.target_number,
          ...parameters
        },
        name: `${t('タクトタイム')}`,
        cancelToken: cancelSource.current.token,
        t
      }).then(datas => {
        setData60006(datas.length > 0 ? datas[0] : []);
      })
    ]).finally(() => {
      setStartProcess(false);
    });
  }, [searchConditions, enqueueSnackbar, t]);

  return (
    <ProgressAria startProcess={startProcess}>
      <LFCTitledCard title={t('本日')} titleSub={`${dispDate} ～ `}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <LFCOkNgPie source={data60351} height="300px" />
          </Grid>
          <Grid item xs={12}>
            <LFCTitledCard title={`${t('検査間隔')}(${t('秒')})`} transparent>
              <LFCSingleValues
                columns={[
                  {
                    field: 'minimum_ct',
                    headerName: `${t('最小')}`,
                    formatter: v => round(v, 1),
                    width: '30%',
                    valVariant: 'h6'
                  },
                  {
                    field: 'average_ct',
                    headerName: `${t('平均')}`,
                    formatter: v => round(v, 1),
                    width: '30%',
                    valVariant: 'h6'
                  },
                  {
                    field: 'maximum_ct',
                    headerName: `${t('最大')}`,
                    formatter: v => round(v, 1),
                    width: '30%',
                    valVariant: 'h6'
                  }
                ]}
                source={data60009}
              />
            </LFCTitledCard>
          </Grid>
          <Grid item xs={12}>
            <LFCTitledCard title={`${t('タクトタイム')}(${t('秒')})`} transparent>
              <LFCSingleValues
                columns={[
                  {
                    field: 'tt_plan',
                    headerName: t('計画'),
                    formatter: v => round(v, 1),
                    width: '46%',
                    valVariant: 'h6'
                  },
                  {
                    field: 'tt_results',
                    headerName: t('実績'),
                    formatter: v => round(v, 1),
                    width: '46%',
                    valVariant: 'h6'
                  }
                ]}
                source={data60006}
              />
            </LFCTitledCard>
          </Grid>
        </Grid>
      </LFCTitledCard>
    </ProgressAria>
  );
};

export default TodaySummaryPanel;
